import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Car, Clock, Users, WhatsappLogo, Phone, Star } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const whatsappMsg = encodeURIComponent("Bonjour, je souhaite organiser le transport pour mon mariage avec ALTURA. Pouvez-vous me faire un devis personnalisé ?");
const whatsappUrl = `https://wa.me/33681969342?text=${whatsappMsg}`;

const MariagePage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Chauffeur Privé Mariage Paris — ALTURA",
    "description": "Service de chauffeur privé pour mariage à Paris et Île-de-France. Mercedes Classe S, Maybach, décoration florale. Transport des mariés et des invités.",
    "url": "https://alturaconciergerie.com/experiences/mariage",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ALTURA Conciergerie",
      "telephone": "+33681969342",
      "address": { "@type": "PostalAddress", "addressLocality": "Paris", "addressCountry": "FR" }
    },
    "areaServed": [
      { "@type": "City", "name": "Paris" },
      { "@type": "AdministrativeArea", "name": "Île-de-France" }
    ],
  };

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="mariage-page">
      <Helmet>
        <title>Chauffeur Privé Mariage Paris | Voiture de Mariage | ALTURA</title>
        <meta name="description" content="Chauffeur privé pour votre mariage à Paris et Île-de-France. Mercedes Classe S décorée, Maybach, transport des mariés et invités. Devis sur mesure." />
        <link rel="canonical" href="https://alturaconciergerie.com/experiences/mariage" />
        <meta property="og:title" content="Chauffeur Privé Mariage Paris | ALTURA" />
        <meta property="og:description" content="Service de transport de luxe pour mariages. Mercedes Classe S, Maybach, décoration florale. Devis personnalisé." />
        <meta property="og:url" content="https://alturaconciergerie.com/experiences/mariage" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_parisgo/artifacts/iuadc8o6_4D3F491D-0EEA-41C6-A7A7-DBFF5456CB20.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero — Mercedes Just Married Place Vendôme */}
      <section className="relative h-[60vh] min-h-[450px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/iuadc8o6_4D3F491D-0EEA-41C6-A7A7-DBFF5456CB20.png"
          alt="Mercedes Classe S décorée pour un mariage — ALTURA Paris"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/40 to-[#1A1A1A]/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">ALTURA · Mariage</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">
                Votre mariage mérite l'excellence
              </h1>
              <p className="text-[#1A1A1A]/70 text-base mt-4 max-w-xl font-light">
                Un service de transport sur mesure pour le plus beau jour de votre vie.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro + CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-6">Un écrin d'exception pour les mariés</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-6">
                Le jour de votre mariage, chaque détail compte. ALTURA vous accompagne avec un service de chauffeur privé d'exception, de la cérémonie à la réception. Nos véhicules sont décorés avec des compositions florales de roses blanches, rubans et voilages, créant une atmosphère romantique et raffinée dès votre montée à bord.
              </p>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">
                Notre chauffeur, en costume impeccable, assure un service discret et ponctuel. Il s'occupe de chaque trajet : mairie, église, lieu de réception, séance photo... Vous n'avez qu'à profiter de votre journée.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="mariage-cta-whatsapp">
                  <WhatsappLogo size={18} weight="fill" /> Devis mariage
                </a>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors inline-flex items-center gap-2" data-testid="mariage-cta-contact">
                  <Phone size={16} /> Nous contacter
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              {/* Photo 2 — cropped to hide license plate (object-position: top) */}
              <img
                src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/63bcravz_59551D9E-268A-4729-AD79-C3C9457A9D35.png"
                alt="Mercedes Classe S fleurs mariage — ALTURA"
                className="w-full h-[400px] object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services inclus */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">Nos prestations</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">Ce que nous offrons</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { id: "decor", icon: <Car size={28} weight="thin" />, title: "Décoration florale", desc: "Nous proposons des compositions florales de roses blanches, rubans et voilages pour décorer votre véhicule. Ce service constitue une option avec frais supplémentaires. Décoration personnalisable selon votre thème." },
              { id: "transport", icon: <Users size={28} weight="thin" />, title: "Transport des invités", desc: "Mercedes Classe V 7 places pour transporter vos invités entre les différents lieux de la cérémonie. Plusieurs véhicules disponibles." },
              { id: "dispo", icon: <Clock size={28} weight="thin" />, title: "Mise à disposition", desc: "Votre chauffeur reste à votre disposition toute la journée : mairie, cérémonie religieuse, séance photo, cocktail, réception." },
              { id: "vehicules", icon: <Star size={28} weight="thin" />, title: "Véhicules de prestige", desc: "Mercedes Classe S, Classe V, Maybach, Range Rover ou Classe G. Choisissez le véhicule qui correspond à l'élégance de votre événement." },
              { id: "perso", icon: <Heart size={28} weight="thin" />, title: "Service personnalisé", desc: "Chaque mariage est unique. Nous adaptons notre prestation à vos besoins : musique, champagne à bord, itinéraire sur mesure." },
              { id: "important", icon: <Phone size={28} weight="thin" />, title: "Important à savoir", desc: "Toute décoration du véhicule doit être signalée et validée avant le jour J. Contactez-nous en amont pour organiser chaque détail sereinement." },
            ].map((service, i) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6"
              >
                <div className="text-[#1A1A1A] mb-4">{service.icon}</div>
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{service.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">L'élégance au service de votre amour</h2>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="col-span-2 lg:col-span-2 row-span-2">
              <img src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/iuadc8o6_4D3F491D-0EEA-41C6-A7A7-DBFF5456CB20.png" alt="Mercedes Classe S mariage Place Vendôme" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <img src="https://images.unsplash.com/photo-1771305129815-fcc1103c47a3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt="Alliances et roses blanches" className="w-full h-48 object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <img src="https://images.unsplash.com/photo-1700157381190-e9fc5ddab91e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt="Décoration florale élégante" className="w-full h-48 object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Déroulé type */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-8">Déroulé type d'une journée mariage</h2>
            <div className="space-y-6">
              {[
                { id: "matin", time: "Matin", title: "Préparatifs", desc: "Votre chauffeur arrive au lieu de préparation des mariés. Le véhicule, impeccablement décoré, est prêt pour les premières photos." },
                { id: "ceremonie", time: "Cérémonie", title: "Mairie & Cérémonie", desc: "Transport des mariés vers la mairie puis vers le lieu de cérémonie (église, château, domaine). Ponctualité irréprochable garantie." },
                { id: "aprem", time: "Après-midi", title: "Séance photo & Cocktail", desc: "Le chauffeur vous conduit aux spots choisis pour votre séance photo. Disponible pour tous les déplacements jusqu'au cocktail." },
                { id: "soiree", time: "Soirée", title: "Réception & Retour", desc: "Transport vers le lieu de réception. En fin de soirée, retour des mariés à leur hôtel ou domicile dans le confort et la discrétion." },
              ].map((step, i) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-6 items-start"
                >
                  <div className="w-24 flex-shrink-0 text-right">
                    <p className="text-[#1A1A1A] text-sm font-medium">{step.time}</p>
                  </div>
                  <div className="w-px bg-[#1A1A1A]/20 min-h-[60px] flex-shrink-0 relative">
                    <div className="absolute top-1 -left-[3px] w-[7px] h-[7px] bg-[#1A1A1A] rounded-full" />
                  </div>
                  <div className="flex-1 pb-4">
                    <h3 className="text-[#1A1A1A] font-medium text-sm mb-1">{step.title}</h3>
                    <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
          <div className="space-y-6">
            {[
              { id: "vehicule", q: "Quel véhicule choisir pour un mariage ?", a: "La Mercedes Classe S est le choix le plus populaire pour les mariés : élégante, spacieuse et raffinée. Pour transporter la famille ou les témoins, la Classe V 7 places est idéale. Pour un mariage encore plus prestigieux, le Maybach offre un niveau de luxe inégalé." },
              { id: "fleurs", q: "La décoration florale est-elle incluse ?", a: "Nous proposons une décoration florale de roses blanches en option. Vous pouvez également faire appel à votre propre fleuriste et nous fournir la décoration. Nous nous chargeons de l'installation." },
              { id: "multi", q: "Peut-on réserver plusieurs véhicules ?", a: "Absolument. Nous pouvons mobiliser plusieurs véhicules pour transporter les mariés, les familles, les témoins et les invités. Chaque véhicule dispose de son chauffeur dédié." },
              { id: "horaires", q: "Jusqu'à quelle heure le chauffeur est-il disponible ?", a: "En mise à disposition, votre chauffeur reste disponible aussi longtemps que nécessaire. La plupart des mariages nécessitent 10 à 14 heures de service. Nous adaptons la prestation à votre planning." },
              { id: "devis", q: "Comment obtenir un devis ?", a: "Contactez-nous par WhatsApp ou téléphone avec la date de votre mariage, le nombre de véhicules souhaités et les lieux de la journée. Nous vous envoyons un devis personnalisé sous 24h." },
            ].map((item) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Faites de votre mariage un moment inoubliable</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Chaque mariage est unique. Contactez-nous pour un devis personnalisé adapté à votre journée.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="mariage-cta-bottom-wa">
              <WhatsappLogo size={18} weight="fill" /> Devis WhatsApp
            </a>
            <a href="tel:+33681969342" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="mariage-cta-bottom-call">
              <Phone size={16} /> +33 6 81 96 93 42
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MariagePage;
